
import { local } from "./drivers/local";
import { session } from "./drivers/session";
import { cookie } from "./drivers/cookie";
import { STORAGE_RULES, StorageKey, StorageType } from "./storageRules";

const drivers: Record<StorageType, any> = {
  local,
  session,
  cookie,
};

function getRules(key: StorageKey) {
  const policy = STORAGE_RULES[key];
  if (!policy) throw new Error(`No storage policy defined for "${key}"`);
  return policy;
}

export const storage = {
  /* ----------------------------------
   * POLICY-BASED READ and return if found in primary else look in replica storage
   * ---------------------------------- */
  get(key: StorageKey) {
    const { primary, replicas } = getRules(key);

    let value = drivers[primary].get(key);
    if (value !== null) return value;

    for (const r of replicas) {
      value = drivers[r].get(key);
    }
    return null;
  },

  /* ----------------------------------
   * READ FROM SPECIFIC STORAGE
   * ---------------------------------- */
  getFrom(key: StorageKey, storage: StorageType) {
    return drivers[storage].get(key);
  },

  /* ----------------------------------
   * READ WITHOUT SIDE EFFECTS
   * ---------------------------------- */
  peek(key: StorageKey) {
    const { primary, replicas } = getRules(key);

    const storages = [primary, ...replicas];
    for (const s of storages) {
      const value = drivers[s].get(key);
      if (value !== null) return value;
    }
    return null;
  },

  /* ----------------------------------
   * CHECK EXISTENCE IN STORAGE
   * ---------------------------------- */
  existsIn(key: StorageKey, storage: StorageType) {
    return drivers[storage].get(key) !== null;
  },

  /* ----------------------------------
   * WRITE (primary + replicas)
   * ---------------------------------- */
  set(key: StorageKey, value: any, options?: any) {
    const { primary, replicas } = getRules(key);

    drivers[primary].set(key, value, options);
    replicas.forEach(r => drivers[r].set(key, value));
  },

  /* ----------------------------------
   * REMOVE EVERYWHERE
   * ---------------------------------- */
  remove(key: StorageKey) {
    const { primary, replicas } = getRules(key);

    drivers[primary].remove(key);
    replicas.forEach(r => drivers[r].remove(key));
  },

  /* ----------------------------------
   * REMOVE FROM ONE STORAGE
   * ---------------------------------- */
  removeFrom(key: StorageKey, storage: StorageType, options?: any) {
    if(storage === "cookie")
      drivers["cookie"].removeWithOption(key, options);
    else
      drivers[storage].remove(key);
  },

  /* ----------------------------------
   * COPY (keep original)
   * ---------------------------------- */
  copy(key: StorageKey, from: StorageType, to: StorageType, options?: any) {
    const value = drivers[from].get(key);
    if (value === null) return;
    drivers[to].set(key, value, options);
  },

  /* ----------------------------------
   * MOVE (copy + delete)
   * ---------------------------------- */
  move(key: StorageKey, from: StorageType, to: StorageType, options?: any) {
    const value = drivers[from].get(key);
    if (value === null) return;
    drivers[to].set(key, value, options);
    drivers[from].remove(key);
  },

  /* ----------------------------------
   * SYNC REPLICAS FROM PRIMARY
   * ---------------------------------- */
  sync(key: StorageKey) {
    const { primary, replicas } = getRules(key);
    const value = drivers[primary].get(key);
    if (value === null) return;
    replicas.forEach(r => drivers[r].set(key, value));
  },

  /* ----------------------------------
   * MIGRATE PRIMARY STORAGE
   * ---------------------------------- */
  migrate(key: StorageKey, to: StorageType) {
    const { primary } = getRules(key);
    this.move(key, primary, to);
  },
};
